import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import { autoinject, bind, FormStore, geocodeLatLng, inject, Input, LocationSearchInput, Map } from 'Mri.Web.Common';
import * as React from 'react';
import { Marker } from '@react-google-maps/api';
import { Collapse, FormGroup } from 'reactstrap';
import goodIcon from '../../../Mri.Web.Common/Assets/Pins/teal-pin.png';
import { AppStore } from '../Core/AppStore';

export interface IDisplayFormLocationProps {
    readonly updateDisplayStore: FormStore<Mri.Web.Portal.Controllers.Displays.UpdateDisplayModel>;
}

@autoinject
@observer
export class DisplayFormLocation extends React.Component<IDisplayFormLocationProps> {
    public async componentDidMount() {
        // This code can be removed in a future version of SmartVu after
        // all lat/lngs have been geocoded in the database
        const lat = this.props.updateDisplayStore.fields.latitude.value;
        const lng = this.props.updateDisplayStore.fields.longitude.value;

        if (!this.address && lat && lng) {
            const geocoderResult = await geocodeLatLng(lat, lng);

            if (geocoderResult) this.updateLocation(lat, lng, geocoderResult);
        }
    }

    public render() {
        return (
            <>
                <div className="col-12">
                    <hr />

                    <h3>Location</h3>
                </div>

                <div className="col-md-6">
                    <FormGroup color="good">
                        <label className="control-label">Closest Street Address</label>

                        <div>
                            <LocationSearchInput onSelect={this.onSelect} value={this.address} />
                        </div>
                    </FormGroup>

                    <FormGroup color={this.props.updateDisplayStore.fields.description.state}>
                        <label className="control-label">Location Description</label>
                        <div>
                            <Input
                                type="text"
                                max={100}
                                field={this.props.updateDisplayStore.fields.description}
                                placeholder="100 Feet SouthEast of 11th & Peacthree"
                            />
                        </div>
                    </FormGroup>

                    <a className="btn btn-link px-0" onClick={this.toggleCollapse}>
                        {this.collapseOpen ? 'Hide Advanced' : 'Advanced'}
                    </a>

                    <Collapse isOpen={this.collapseOpen}>
                        <FormGroup color={this.props.updateDisplayStore.fields.address1.state}>
                            <label className="control-label">Street Address</label>
                            <div>
                                <Input
                                    type="text"
                                    max={120}
                                    field={this.props.updateDisplayStore.fields.address1}
                                    placeholder="123 Abc street"
                                />
                            </div>
                        </FormGroup>

                        <FormGroup color={this.props.updateDisplayStore.fields.city.state}>
                            <label className="control-label">City</label>
                            <div>
                                <Input
                                    type="text"
                                    max={50}
                                    field={this.props.updateDisplayStore.fields.city}
                                    placeholder="City"
                                />
                            </div>
                        </FormGroup>

                        <FormGroup color={this.props.updateDisplayStore.fields.country.state}>
                            <label className="control-label">Country</label>
                            <div>
                                <Input
                                    type="text"
                                    max={50}
                                    field={this.props.updateDisplayStore.fields.country}
                                    placeholder="USA"
                                />
                            </div>
                        </FormGroup>

                        <FormGroup color={this.props.updateDisplayStore.fields.stateOrProvince.state}>
                            <label className="control-label">State Or Province</label>
                            <div>
                                <Input
                                    type="text"
                                    max={50}
                                    field={this.props.updateDisplayStore.fields.stateOrProvince}
                                    placeholder="State/province"
                                />
                            </div>
                        </FormGroup>

                        <FormGroup color={this.props.updateDisplayStore.fields.postalCode.state}>
                            <label className="control-label">Postal Code</label>
                            <div>
                                <Input
                                    type="text"
                                    max={12}
                                    field={this.props.updateDisplayStore.fields.postalCode}
                                    placeholder="Zip"
                                />
                            </div>
                        </FormGroup>
                    </Collapse>
                </div>

                <div className="col-md-6">
                    <Map
                        dark={this.appStore.storage.theme == 'dark-theme'}
                        defaultCenter={this.defaultCenter}
                        zoom={17}
                        recenter
                    >
                        {this.props.updateDisplayStore.fields.latitude.value &&
                            this.props.updateDisplayStore.fields.longitude.value && (
                            <Marker
                                onDragEnd={this.markerMoved}
                                position={{
                                    lat: this.props.updateDisplayStore.fields.latitude.value,
                                    lng: this.props.updateDisplayStore.fields.longitude.value,
                                }}
                                draggable
                                icon={goodIcon}
                            />
                        )}
                    </Map>
                </div>
            </>
        );
    }

    @computed
    private get address() {
        const fields = this.props.updateDisplayStore.fields;
        const components = [
            fields.address1.value,
            fields.address2.value,
            fields.city.value,
            fields.stateOrProvince.value,
            fields.postalCode.value,
            fields.country.value,
        ];
        return components.filter(c => !!c).join(', ');
    }

    @computed
    private get defaultCenter() {
        return {
            lat: this.props.updateDisplayStore.fields.latitude.value ?? 34.1123969,
            lng: this.props.updateDisplayStore.fields.longitude.value ?? -84.195299,
        };
    }

    @bind
    private async markerMoved(data: google.maps.MouseEvent) {
        const lat = data.latLng.lat();
        const lng = data.latLng.lng();

        const geocoderResult = await geocodeLatLng(lat, lng);

        if (geocoderResult) this.updateLocation(lat, lng, geocoderResult);
    }

    @bind
    private onSelect(address: string, lat: number, lng: number, geocoderResult: google.maps.GeocoderResult) {
        if (geocoderResult) this.updateLocation(lat, lng, geocoderResult);
    }

    @bind
    private updateLocation(lat: number, lng: number, geocoderResult: google.maps.GeocoderResult) {
        const fields = this.props.updateDisplayStore.fields;
        fields.latitude.setValue(lat);
        fields.longitude.setValue(lng);

        const addressComponents = geocoderResult.address_components;
        const streetAddress = addressComponents.find(a => a.types.includes('street_number'));
        const route = addressComponents.find(a => a.types.includes('route'));
        const city = addressComponents.find(a => a.types.includes('locality'));
        const stateOrProvince = addressComponents.find(a => a.types.includes('administrative_area_level_1'));
        const country = addressComponents.find(a => a.types.includes('country'));
        const postalCode = addressComponents.find(a => a.types.includes('postal_code'));

        let combinedAddress: string | undefined;

        if (route && streetAddress) combinedAddress = `${streetAddress.long_name} ${route.long_name}`;
        else if (route && !streetAddress) combinedAddress = route.long_name;
        else if (!route && streetAddress) combinedAddress = streetAddress.long_name;
        else combinedAddress = undefined;

        if (combinedAddress) fields.address1.setValue(combinedAddress);
        else fields.address1.clear();

        if (city) fields.city.setValue(city.long_name);
        else fields.city.clear();

        if (stateOrProvince) fields.stateOrProvince.setValue(stateOrProvince.long_name);
        else fields.stateOrProvince.clear();

        if (country) fields.country.setValue(country.long_name);
        else fields.country.clear();

        if (postalCode) fields.postalCode.setValue(postalCode.long_name);
        else fields.postalCode.clear();

        // Clear out address2
        fields.address2.clear();
    }

    @bind
    @action
    private toggleCollapse() {
        this.collapseOpen = !this.collapseOpen;
    }

    @observable
    private collapseOpen: boolean = false;

    @inject
    private appStore!: AppStore;
}
